<template>
  <!-- Product Overview Modal -->
  <!--<button @click="closeModal">close</button>-->
  <div class="overview-modal-bg" v-show="modalState.isVisible" @click="closeModal"></div>
  <div class="overview-modal" :class="{ 'show': modalState.isVisible }"
    :id="`product-overview-modal-${product.productId}`">
    <button class="close-modal" @click="closeModal"><i class="fa-regular fa-xmark"></i></button>
    <div class="d-flex flex-row flex-wrap">
      <div class="col-12 col-lg-5 d-flex flex-column justify-content-center pe-lg-4">
        <div class="img-block" :style="isUsingFallback ? { backgroundColor: '#ffffff' } : {}">
          <div :key="product.productId" class="bg-square bg-contain" :style="{ backgroundImage: cssUrl(displayImage) }"></div>
          <div class="mini-gallery d-none">
            <div class="gallery-image" @click="updateImage('/images/work-shoe.png')">
              <img src="/images/work-shoe.png" alt="">
            </div>
            <div class="gallery-image" @click="updateImage('/images/explore-offer-deals.png')">
              <img src="/images/explore-offer-deals.png" alt="">
            </div>
            <div class="more-images">+3</div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-7 p-4 py-lg-8">

        <div class="stock-container d-flex flex-row align-items-center">
          <div class="stock text-secondary" v-if="product.inStock">
            <i class="fa-sharp fa-solid fa-truck-fast"></i>
            <span class="text-uppercase h6 mb-0 ms-2">In stock</span>
          </div>

          <div class="stock text-day-glo" v-else-if="!product.hasFinish">
            <i class="fa-sharp fa-regular fa-cart-xmark"></i>
            <span class="text-uppercase h6 mb-0 ms-2">Special Order Item</span>
          </div>

          <div class="delivery ms-2 mt-1" v-if="product.inStock && !product.hasFinish">
            <p class="text-primary h6 fw-normal mb-0">Next Day Delivery</p>
          </div>
        </div>

        <h4 class="text-primary fw-bold my-1 pe-8">{{ product.name }}</h4>

        <div class="d-flex flex-row align-items-center">
          <h3 class="fs-small text-secondary fw-normal mb-0">{{ product.category }}</h3>
          <p class="mb-0 px-2 text-secondary"><i class="fa-sharp fa-thin fa-pipe"></i></p>
          <p class="fs-small fw-normal text-grey mb-0">Product code: <span class="fw-extra">{{
            product.productCode }}</span></p>
        </div>

        <p class="fs-small fw-normal text-primary my-6">{{ product.longDesc }}</p>

        <div class="details-container d-flex flex-row flex-wrap align-items-center">

          <Badge v-for="enStandard in product.enStandards" type="badge-alt-green" icon="me-1 fa-regular fa-circle-check"
            class="fw-light text-primary py-1 mb-2 mb-lg-4 me-2" small rounded>
            {{enStandard}}
          </Badge>

        </div>

        <div class="icon-container my-6">
          <div v-for="feature in product.features" class="icon mb-2 mb-lg-4 d-flex align-items-center">
            <div v-if="feature.heading && feature.subheading">
              <div class="main-icon">
                <i class="fa-sharp fa-fw fa-light fa-check"></i>
              </div>
              <div class="d-flex flex-sm-row flex-column">
                <p class="d-sm-block d-none fs-small fw-normal mb-0 ms-4">{{feature.heading}}</p>
                <p class="d-sm-none d-block fs-small fw-semi-bold mb-0 ms-4">{{feature.heading}}</p>
                <p class="fs-small fw-normal mb-0 text-grey d-flex flex-row align-items-center ms-4">
                  <i class="d-sm-inline d-none fa-solid fa-circle me-4"></i>
                  {{feature.subheading}}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex flex-md-row flex-column align-items-md-center">
          <div class="price-container d-flex flex-row align-items-center me-md-4 mb-md-0 mb-4">
            <span class="h6 fw-normal text-primary mb-0 me-2">From:</span>
            <h4 class="text-dark-green mb-0" v-if="hasPointsBudget">{{ calculateItemPoints(product) }} pts</h4>
            <h4 class="text-dark-green mb-0" v-else>£{{ parseFloat(product.fromPrice).toFixed(2) }}</h4>
          </div>


          <qty-selector v-if="product.showQuickAdd" :quantity="product.qty" class="me-md-4 mb-md-0 mb-4"
            @increase-qty="product.qty++" @reduce-qty="product.qty--">
            <Button v-if="!viewOnly" btn-type="btn-secondary" icon-last="fa-regular fa-sharp fa-cart-shopping-fast" small
              class="quick-add-btn text-white mb-0 py-0" @click="addToBasket()" basic-hover>
              Quick Add
            </Button>
          </qty-selector>

            <Button @click="viewProduct(product.link)" btn-type="btn-primary" class="modal-btn small" small icon-last="fa-regular fa-circle-info">
              More Info
            </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Button from '../Elements/Button.vue'
import Badge from '../Elements/Badge.vue'
import QtySelector from '@/components/Elements/QtySelector.vue'
import cssUrl from '@/helpers/css-url'

const router = useRouter();

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const modalState = inject('modalState');
const closeModal = inject('closeModal');

const store = useStore();
const currentImage = ref('/images/work-shoe.png');

const hasPointsBudget = computed(() => store.getters['account/hasPointsBudget']);
const pointsMultiplier = computed(() => store.getters['account/pointsMultiplier']);
const viewOnly = computed(() => store.getters['account/viewOnly']);

const calculateItemPoints = (product) => {
  if (product.budget && product.budget.itemPoints) {
    return product.budget.itemPoints.toLocaleString()
  }
  if (pointsMultiplier.value > 0 && product.price) {
    return Math.round(product.price * pointsMultiplier.value).toLocaleString()
  }
  return '0'
};

const updateImage = (image) => {
  currentImage.value = image;
}

const fallbackImage = '/images/smi-logo.svg';

const selectedImage = computed(() => {
  const colorVariants = props.product.colors || [];

  if (!props.product.chosenColor || !Array.isArray(colorVariants)) {
    return props.product.image;
  }

  const matched = colorVariants.find(variant =>
    variant.color?.toLowerCase() === props.product.chosenColor.toLowerCase()
  );

  return matched?.images?.[0] || props.product.image;
});

const isUsingFallback = computed(() => !selectedImage.value);

const displayImage = computed(() => selectedImage.value || fallbackImage);

const addToBasket = async () => {
  if (viewOnly.value) return;

  const product = props.product;

  const addToBasketObject = {
    price: product.fromPrice,
    qty: product.qty,
    productId: product.productId,
    parentId: product.productId,
    name: product.name
  }

  await store.dispatch('basket/addToBasket', addToBasketObject);
  await store.dispatch('basket/orderStockFetchData')
  // reset qty
  product.qty = 0;
}

const viewProduct = (link) => {
  closeModal()
  router.push(link)
}
</script>
